import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  subscribePosCustomSort, buildPosCustomOrder, movePosCustomOrder, savePosCustomSort,
} from '../utils/posCustomSortUtil';

/**
 * The hand-arranged ("Custom") order for one product list, and the unsaved
 * edits made to it by dragging.
 *
 * The saved order is only read while Custom is the chosen sort — the same
 * lazy rule the "Items sold" order follows for the orders node. Edits live in
 * a local draft until `save()`, so a half-finished arrangement never reaches
 * another till; switching away from Custom drops the draft.
 *
 * @param {object} params
 * @param {string} params.clientId
 * @param {string} params.listKey - a POS_CUSTOM_SORT_LISTS value
 * @param {boolean} params.active - Custom is the sort in force
 * @param {object[]} params.products - the WHOLE product list, not the filtered one
 * @param {string} [params.actor] - the staff address to record on save
 * @returns {{order: string[], dirty: boolean, wiggle: boolean, saving: boolean, move: (fromId: string, toId: string) => void, save: () => Promise<void>, discard: () => void}}
 *   `wiggle` is true only while there are unsaved drags: the grid stays still
 *   when Custom is chosen and starts moving once someone starts arranging,
 *   until the arrangement is saved.
 */
function useCustomSort({ clientId, listKey, active, products, actor = '' }) {
  const [saved, setSaved] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!active) {
      setDraft(null);
      return undefined;
    }
    return subscribePosCustomSort(clientId, listKey, setSaved);
  }, [clientId, listKey, active]);

  // Rebuilt against the live list, so a product added or removed on another
  // till appears in (or leaves) the arrangement without a reload.
  const order = useMemo(
    () => buildPosCustomOrder(draft || saved, products),
    [draft, saved, products],
  );

  /**
   * Moves one product onto another's place, in the draft.
   * @param {string} fromId
   * @param {string} toId
   * @returns {void}
   */
  const move = useCallback((fromId, toId) => {
    setDraft((current) => movePosCustomOrder(buildPosCustomOrder(current || saved, products), fromId, toId));
  }, [saved, products]);

  /**
   * Saves the draft as this list's custom order.
   * @returns {Promise<void>}
   * @throws {Error} from savePosCustomSort — the caller reports it
   */
  const save = useCallback(async () => {
    setSaving(true);
    try {
      const result = await savePosCustomSort(clientId, listKey, order, actor);
      setSaved(result);
      setDraft(null);
    } finally {
      setSaving(false);
    }
  }, [clientId, listKey, order, actor]);

  /** Throws away unsaved drags. @returns {void} */
  const discard = useCallback(() => setDraft(null), []);

  const dirty = draft !== null;
  return { order, dirty, wiggle: dirty, saving, move, save, discard };
}

export default useCustomSort;
